import type { ShubhMuhuratResponse } from "@/lib/api";

/**
 * Server-rendered display for the curated shubh-muhurat digest. Shared by the
 * en + hi city pages so the two locales render the same component tree (the
 * monetization/value moment can never be left behind on one locale — rule 46).
 * All values come from the engine digest (rule 29); times are tz-aware (rule 25).
 */

type Locale = "en" | "hi";

export function todayISTDateString(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

import LiveTimingCountdown from "@/components/engine-visuals/LiveTimingCountdown";

/** One-line "best window today" verdict — the page's distinct value moment. */
export function BestWindowVerdict({
  digest,
  cityName,
  locale = "en",
}: {
  digest: ShubhMuhuratResponse | null;
  cityName: string;
  locale?: Locale;
}) {
  const best = digest?.recommended_windows?.[0];
  if (!best) return null;
  const hi = locale === "hi";
  const today = todayISTDateString();
  const endsAtISO =
    best.end && /^\d{2}:\d{2}/.test(best.end)
      ? `${today}T${best.end.slice(0, 5)}:00+05:30`
      : undefined;

  return (
    <div className="card-sacred mb-6 border border-border-interactive p-4">
      <p className="mb-1 text-caption font-semibold uppercase tracking-eyebrow text-intent-brand">
        {hi ? "आज का श्रेष्ठ मुहूर्त" : "Best window today"}
      </p>
      <p className="text-body-small font-semibold text-text-primary">
        {hi
          ? `${cityName} में ${best.name_hi} ${best.start}–${best.end} का सुझाया गया सुरक्षित समय है।`
          : `For ${digest.purpose_label_en.toLowerCase()} in ${cityName}, ${best.name_en} (${best.start}–${best.end}) is the first avoid-window-safe choice.`}
      </p>
      {endsAtISO && (
        <div className="mt-3">
          <LiveTimingCountdown
            labelEn={best.name_en}
            labelHi={best.name_hi}
            endsAt={endsAtISO}
            isAuspicious={true}
            locale={locale}
          />
        </div>
      )}
    </div>
  );
}

export function ShubhMuhuratDigest({
  digest,
  locale = "en",
}: {
  digest: ShubhMuhuratResponse | null;
  locale?: Locale;
}) {
  const hi = locale === "hi";
  if (!digest) {
    return (
      <p className="mb-6 text-body-small text-text-muted" data-ishvaram-degraded="muhurat-digest">
        {hi
          ? "मुहूर्त डेटा अभी लोड नहीं हो सका। कृपया कुछ देर बाद पुनः प्रयास करें।"
          : "Muhurat data could not load right now. Please try again shortly."}
      </p>
    );
  }

  return (
    <>
      {(digest.recommended_windows?.length ?? 0) > 0 && (
        <>
          <DigestSubhead>
            {hi
              ? `${digest.purpose_label_hi} के लिए सुझाए गए समय`
              : `Recommended for ${digest.purpose_label_en.toLowerCase()}`}
          </DigestSubhead>
          <div className="mb-6 grid gap-2 sm:grid-cols-2">
            {digest.recommended_windows.slice(0, 4).map((window, index) => (
              <div key={window.key} className="card-sacred p-3.5">
                <p className="text-caption font-semibold uppercase tracking-eyebrow text-intent-brand">
                  {index === 0 ? (hi ? "पहला विकल्प" : "First choice") : hi ? "विकल्प" : "Option"}
                </p>
                <p className="mt-1 text-body-small font-semibold text-text-primary">
                  {hi ? window.name_hi : window.name_en}
                </p>
                <p className="mt-1 text-body-small text-text-muted">
                  {window.start}–{window.end}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
      <DigestSubhead>{hi ? "आज के शुभ मुहूर्त" : "Today's Auspicious Muhurats"}</DigestSubhead>
      <div className="space-y-2 mb-6">
        {digest.named_muhurats.map((m) => (
          <div key={m.key} className="card-sacred p-3.5 flex items-start justify-between gap-3">
            <div>
              <p className="text-body-small font-semibold text-text-primary">
                {hi ? m.name_hi : m.name_en}
              </p>
              <p className="mt-1 text-caption text-text-muted">
                {hi ? m.summary_hi : m.summary_en}
              </p>
            </div>
            <span className="whitespace-nowrap text-body-small font-semibold text-intent-brand">
              {m.start}–{m.end}
            </span>
          </div>
        ))}
      </div>

      {digest.auspicious_choghadiya.length > 0 && (
        <>
          <DigestSubhead>
            {hi ? "श्रेष्ठ चौघड़िया अवधियाँ" : "Best Choghadiya Windows"}
          </DigestSubhead>
          <div className="flex flex-wrap gap-2 mb-6">
            {digest.auspicious_choghadiya.map((c, i) => (
              <span
                key={`${c.name_en}-${c.start}-${i}`}
                className="rounded-lg border border-border-interactive bg-surface-brand-subtle px-3 py-1.5 text-caption font-medium text-intent-brand"
              >
                {hi ? c.name_hi : c.name_en} {c.start}–{c.end}
              </span>
            ))}
          </div>
        </>
      )}

      <DigestSubhead>{hi ? "इन समयों से बचें" : "Times to Avoid"}</DigestSubhead>
      <div className="space-y-2 mb-6">
        {digest.inauspicious.map((w) => (
          <div key={w.key} className="card-sacred p-3.5 flex items-center justify-between gap-3">
            <p className="text-body-small font-semibold text-text-primary">
              {hi ? w.name_hi : w.name_en}
            </p>
            <span className="whitespace-nowrap text-body-small font-semibold text-text-muted">
              {w.start}–{w.end}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function DigestSubhead({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 mt-2 font-serif text-title font-bold text-text-primary">{children}</h2>
  );
}
